import { LOGO_URL, LOGO_URL_DARK, LOGO_URL_TINTED } from './constants';

/**
 * Onboarding header — Grassroots logo + match-themed progress tracker
 * (football rolling along a track toward a white goal, KO/HT/FT labels).
 *
 * Theme-aware:
 *   - Dark theme / dark-tinted page bg → white logo, white track tints, white goal stroke
 *   - Light theme / light-tinted page bg (white, gold) → green-clover logo, dark tracker
 */
export default function OnboardingHeader({
  theme,
  page,
  TOTAL_PAGES,
  enabledPagesLength,
  hasPrimaryPick,
  isLightPagebg,
  animationsEnabled,
  direction,
  logoSpinKey,
}) {
  const stagger = page === 1 && animationsEnabled && direction === 'forward';
  // Football reaches the goal (100%) on the final setup page; the plan page
  // (page === TOTAL_PAGES) keeps the ball at the goal too. We therefore use
  // enabledPagesLength as the denominator instead of TOTAL_PAGES.
  const denom = Math.max(1, (enabledPagesLength || (TOTAL_PAGES - 1)) - 1);
  const ballPercent = Math.min(100, Math.max(0, ((page - 1) / denom) * 100));
  const ballRotation = (page - 1) * 360;

  // Logo selection
  const logoSrc = hasPrimaryPick && !isLightPagebg
    ? LOGO_URL_TINTED
    : (theme === 'dark' ? LOGO_URL_DARK : LOGO_URL);

  // Tracker theme palette
  const isLightTheme = theme === 'light';
  const isLightView = isLightPagebg || (!hasPrimaryPick && isLightTheme);
  const goalStroke = isLightView ? '#0F172A' : '#FFFFFF';
  // Light theme: render the inner net lines as a soft slate so they don't
  // look like dark stripes cutting through the ball.
  const netStroke = isLightView ? '#94A3B8' : '#FFFFFF';
  const netOpacity = isLightView ? 0.35 : 0.4;
  const trackDimClass = isLightView ? 'bg-gray-300' : 'bg-white/20';
  const stopDimClass = isLightView ? 'bg-gray-300' : 'bg-white/30';
  const progressFillClass = (hasPrimaryPick && !isLightPagebg) ? 'bg-white' : 'bg-emerald-700';
  const labelDimClass = isLightView ? 'text-gray-500' : 'text-white/55';
  const labelActiveClass = isLightView ? 'text-gray-900' : 'text-white';
  const matchLabels = ['KO', 'HT', 'FT'];

  return (
    <div className="pt-8 pb-0 sm:pb-2 flex-shrink-0">
      {/* Logo */}
      <div className="text-center mb-6 animate-fadeIn">
        <img
          key={animationsEnabled ? logoSpinKey : 'static'}
          src={logoSrc}
          alt="Logo"
          className={`w-20 h-20 mx-auto mb-3 object-contain ${animationsEnabled ? 'animate-onbLogoSpin' : ''}`}
        />
      </div>

      {/* Step indicator — football rolls along a line toward a goal (KO → HT → FT) */}
      <div className="mb-6" data-testid="onboarding-steps">
        <div className="relative w-full max-w-[260px] mx-auto">
          {/* Track row */}
          <div className="relative h-10">
            {/* Background line */}
            <div
              className={`absolute top-1/2 h-px -translate-y-1/2 rounded-full ${trackDimClass} ${stagger ? 'animate-onbTrackDraw' : ''}`}
              style={{ left: '18px', right: '18px' }}
            />
            {/* Progress fill */}
            <div
              className={`absolute top-1/2 h-[2px] -translate-y-1/2 rounded-full ${progressFillClass}`}
              style={{
                left: '18px',
                width: `calc((100% - 36px) * ${ballPercent / 100})`,
                transition: 'width 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
            {/* Match milestone stops */}
            {matchLabels.map((_, i) => {
              const stopPercent = (i / (matchLabels.length - 1)) * 100;
              const reached = ballPercent >= stopPercent - 0.5;
              return (
                <div
                  key={i}
                  className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full transition-all duration-500 ${
                    reached ? `w-2 h-2 ${progressFillClass}` : `w-1.5 h-1.5 ${stopDimClass}`
                  }`}
                  style={{ left: `calc(18px + (100% - 36px) * ${stopPercent / 100})` }}
                  data-testid={`onboarding-step-stop-${i + 1}`}
                />
              );
            })}

            {/* Football — rolls along the track. The outer <div> only handles
                position (left). Rotation is applied to an inner <g> so the
                3D top-left highlight (radial gradient) stays fixed at the top
                while only the pentagon + spokes spin like a real rolling ball. */}
            <div
              className={`absolute top-1/2 z-10 ${stagger ? 'animate-onbBallRollIn' : ''}`}
              style={{
                left: `calc(18px + (100% - 36px) * ${ballPercent / 100})`,
                transform: 'translate(-50%, -50%)',
                transition: 'left 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                ...(stagger ? { animationDelay: '150ms' } : {}),
              }}
              data-testid="onboarding-football"
            >
              <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <radialGradient id="onbBallShade" cx="35%" cy="30%" r="70%">
                    <stop offset="0%" stopColor="#FFFFFF" />
                    <stop offset="70%" stopColor="#E2E8F0" />
                    <stop offset="100%" stopColor="#94A3B8" />
                  </radialGradient>
                </defs>
                {/* Sphere shading stays fixed — gives the ball a permanent
                    "lit from top-left" look regardless of rolling rotation. */}
                <circle cx="24" cy="24" r="20" fill="url(#onbBallShade)" stroke="#0F172A" strokeWidth="1.5" />
                {/* Pattern (pentagon + spokes) rotates to fake the rolling motion. */}
                <g
                  style={{
                    transform: `rotate(${ballRotation}deg)`,
                    transformOrigin: '24px 24px',
                    transition: 'transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <polygon points="24,14 32,19.5 29,28 19,28 16,19.5" fill="#0F172A" />
                  <path d="M24 14 L24 6" stroke="#0F172A" strokeWidth="1.25" />
                  <path d="M32 19.5 L39 17.5" stroke="#0F172A" strokeWidth="1.25" />
                  <path d="M29 28 L34 33" stroke="#0F172A" strokeWidth="1.25" />
                  <path d="M19 28 L14 33" stroke="#0F172A" strokeWidth="1.25" />
                  <path d="M16 19.5 L9 17.5" stroke="#0F172A" strokeWidth="1.25" />
                </g>
              </svg>
            </div>

            {/* Goal NET (rendered BEHIND the ball at z-5 so net lines never
                show through the ball — fixes light-theme "transparent ball" look) */}
            <div
              className={`absolute top-1/2 right-0 -translate-y-1/2 z-[5] ${stagger ? 'animate-onbGoalSlideInFromRight' : ''}`}
              style={stagger ? { animationDelay: '300ms' } : undefined}
              data-testid="onboarding-net"
              aria-hidden
            >
              <svg width="36" height="22" viewBox="0 0 64 48" xmlns="http://www.w3.org/2000/svg">
                <line x1="18" y1="12" x2="18" y2="40" stroke={netStroke} strokeWidth="0.75" opacity={netOpacity} />
                <line x1="26" y1="12" x2="26" y2="40" stroke={netStroke} strokeWidth="0.75" opacity={netOpacity} />
                <line x1="32" y1="12" x2="32" y2="40" stroke={netStroke} strokeWidth="0.75" opacity={netOpacity} />
                <line x1="38" y1="12" x2="38" y2="40" stroke={netStroke} strokeWidth="0.75" opacity={netOpacity} />
                <line x1="46" y1="12" x2="46" y2="40" stroke={netStroke} strokeWidth="0.75" opacity={netOpacity} />
                <line x1="10" y1="22" x2="54" y2="22" stroke={netStroke} strokeWidth="0.75" opacity={netOpacity} />
                <line x1="10" y1="32" x2="54" y2="32" stroke={netStroke} strokeWidth="0.75" opacity={netOpacity} />
              </svg>
            </div>

            {/* Goal FRAME (posts + crossbar, in front of ball at z-20) */}
            <div
              className={`absolute top-1/2 right-0 -translate-y-1/2 z-20 ${stagger ? 'animate-onbGoalSlideInFromRight' : ''}`}
              style={stagger ? { animationDelay: '300ms' } : undefined}
              data-testid="onboarding-goal"
            >
              <svg width="36" height="22" viewBox="0 0 64 48" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 12 L10 40" stroke={goalStroke} strokeWidth="2.5" strokeLinecap="round" />
                <path d="M54 12 L54 40" stroke={goalStroke} strokeWidth="2.5" strokeLinecap="round" />
                <path d="M10 12 L54 12" stroke={goalStroke} strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </div>
          </div>

          {/* Match-themed labels: KO / HT / FT */}
          <div className="relative mt-1">
            {matchLabels.map((label, i) => {
              const stopPercent = (i / (matchLabels.length - 1)) * 100;
              const reached = ballPercent >= stopPercent - 0.5;
              return (
                <span
                  key={i}
                  className={`absolute top-0 -translate-x-1/2 text-[10px] font-semibold tracking-wider transition-colors duration-500 ${
                    reached ? labelActiveClass : labelDimClass
                  } ${stagger ? 'animate-fadeIn opacity-0' : ''}`}
                  style={{
                    left: `calc(18px + (100% - 36px) * ${stopPercent / 100})`,
                    ...(stagger ? { animationDelay: `${i * 200 + 200}ms`, animationFillMode: 'forwards' } : {}),
                  }}
                  data-testid={`onboarding-step-label-${i + 1}`}
                >
                  {label}
                </span>
              );
            })}
            <span className="invisible text-[10px] font-semibold tracking-wider">FT</span>
          </div>
        </div>
      </div>
    </div>
  );
}
